import { useEffect, useState, PropsWithChildren } from 'react'
import { useAuth } from '../modules/auth/contexts/Auth'
import { Role } from '../models/Role'
import { enumFromStringValue, Permission } from '../utils/permissions'
import { Admin } from '../models/Admin'

export interface HasAccessProps {
  roles?: Role | Role[]
  permissions?: Permission | Permission[]
  isLoading?: React.ReactNode
  renderAuthFailed?: React.ReactNode
}

const HasAccess = ({
  roles,
  permissions,
  isLoading,
  renderAuthFailed,
  children,
}: PropsWithChildren<HasAccessProps>) => {
  const { currentUser } = useAuth<Admin, {}>()
  const [hasAccess, setHasAccess] = useState<boolean>(false)
  const [checking, setChecking] = useState<boolean>(false)

  useEffect(() => {
    if (!currentUser) {
      console.error(
        'No admin provided! You must set admin to perfom access check'
      )
      return
    }

    setChecking(true)

    // Support admin has all access
    if (userHasRoles(currentUser, [])) {
      setHasAccess(true)
    }

    // role check
    if (roles) {
      const rolesToCheck = Array.isArray(roles) ? roles : [roles]
      if (
        rolesToCheck &&
        currentUser.roles &&
        currentUser.roles.length > 0 &&
        userHasRoles(currentUser, rolesToCheck)
      ) {
        setHasAccess(true)
      }
    }

    // permission check
    if (permissions) {
      const permissionsToCheck = Array.isArray(permissions)
        ? permissions
        : [permissions]
      if (
        permissionsToCheck &&
        currentUser.permissions &&
        currentUser.permissions.length > 0 &&
        userHasPermissions(currentUser, permissionsToCheck)
      ) {
        setHasAccess(true)
      }
    }

    setChecking(false)
  }, [roles, permissions, currentUser])

  if (!hasAccess && checking) {
    return <>{isLoading}</>
  }

  if (hasAccess) {
    return <>{children}</>
  }

  if (renderAuthFailed) {
    return <>{renderAuthFailed}</>
  }

  return <></>
}

export default HasAccess

export const useHasAccess = () => {
  const { currentUser } = useAuth<Admin, {}>()

  const hasAccess = (permissions: Permission | Permission[]): boolean => {
    if (!currentUser) {
      console.error(
        'No admin provided! You must set admin to perfom access check'
      )
      return false
    }

    // Support has all access
    if (userHasRoles(currentUser, [])) return true

    if (!currentUser.permissions || currentUser.permissions.length === 0) {
      return false
    }

    const permissionsToCheck = Array.isArray(permissions)
      ? permissions
      : [permissions]

    if (!permissionsToCheck || permissionsToCheck.length === 0) {
      console.error(
        'No permissions to check! Please provide at least one permission'
      )
      return false
    }

    // Permissions check
    return userHasPermissions(currentUser, permissionsToCheck)
  }
  return { hasAccess }
}

const userHasPermissions = (admin: Admin, permissions: Permission[]): boolean =>
  admin.permissions.filter((permission: string) => {
    const plethorePermissions = enumFromStringValue(Permission, permission)
    return (
      plethorePermissions !== undefined &&
      permissions.includes(plethorePermissions)
    )
  }).length > 0

export const userHasRoles = (admin: Admin, roles: Role[]): boolean => {
  if (!admin.roles) return false
  // Support has all access
  if (admin.roles.map((r) => r.name).includes('support')) return true

  const roleNames = roles.map((r) => r.name)
  return (
    admin.roles.filter((role: Role) => roleNames.includes(role.name)).length > 0
  )
}
